// Store de módulo (singleton) para la resolución de cada feature. Existe para que
// TODAS las instancias del hook `useFeatureVersion(featureKey)` compartan UN solo estado:
// el gate del router (que decide qué página renderizar) y la card de invitación viven en
// instancias separadas del hook. Sin estado compartido, el switch optimista de la card no
// re-renderizaba el gate y la página seguía en V1 hasta navegar/recargar (defecto funcional).
//
// `useSyncExternalStore` consume este store: cualquier write notifica a TODOS los
// suscriptores del mismo featureKey en el mismo tick, así el flip de variante re-monta el
// subtree del menú al instante (corrección VINCULANTE #6 del plan, sin navigate ni reload).

import { readCache } from './cache';
import type { FeatureVersionCacheEntry } from './types';

export interface FeatureVersionState {
  entry: FeatureVersionCacheEntry;
  // `resolved` = ya hidratamos desde el backend al menos una vez en esta sesión.
  resolved: boolean;
}

type Listener = () => void;

const controlFallbackEntry = (featureKey: string): FeatureVersionCacheEntry => ({
  feature_key: featureKey,
  variant: 'control',
  can_revert: false,
  ga_forced: false,
  menu_state: null,
  experiment_id: null,
  updated_at: 0,
});

// Un slot de estado + sus listeners por featureKey. El snapshot es estable entre renders
// (misma referencia) salvo que un write lo reemplace → useSyncExternalStore no parpadea.
interface Slot {
  state: FeatureVersionState;
  listeners: Set<Listener>;
}

const slots = new Map<string, Slot>();

function getSlot(featureKey: string): Slot {
  let slot = slots.get(featureKey);
  if (!slot) {
    // Inicialización SÍNCRONA desde cache local (0ms). Si no hay cache → control.
    const cached = readCache(featureKey);
    slot = {
      state: {
        entry: cached ?? controlFallbackEntry(featureKey),
        resolved: Boolean(cached),
      },
      listeners: new Set(),
    };
    slots.set(featureKey, slot);
  }
  return slot;
}

// Snapshot estable (misma referencia mientras no haya write) para useSyncExternalStore.
export function getStoreSnapshot(featureKey: string): FeatureVersionState {
  return getSlot(featureKey).state;
}

export function subscribeStore(featureKey: string, listener: Listener): () => void {
  const slot = getSlot(featureKey);
  slot.listeners.add(listener);
  return () => {
    slot.listeners.delete(listener);
  };
}

function notify(slot: Slot): void {
  slot.listeners.forEach((l) => l());
}

// Reemplaza el snapshot solo si algo cambió (igualdad campo a campo) y notifica a todas
// las instancias del hook. Devuelve true si hubo cambio efectivo.
export function setStoreEntry(featureKey: string, next: FeatureVersionCacheEntry): boolean {
  const slot = getSlot(featureKey);
  const prev = slot.state.entry;
  const unchanged =
    prev.variant === next.variant &&
    prev.can_revert === next.can_revert &&
    prev.ga_forced === next.ga_forced &&
    prev.menu_state === next.menu_state &&
    prev.experiment_id === next.experiment_id;
  if (unchanged) return false;
  slot.state = { entry: next, resolved: slot.state.resolved };
  notify(slot);
  return true;
}

export function setStoreResolved(featureKey: string, resolved: boolean): void {
  const slot = getSlot(featureKey);
  if (slot.state.resolved === resolved) return;
  slot.state = { entry: slot.state.entry, resolved };
  notify(slot);
}

// Reset de PRODUCCIÓN: limpia el singleton al cambiar de sesión/sucursal (login, logout,
// refreshLogin). Sin esto, el `slots` de módulo (keyed solo por featureKey) conserva la
// resolución del comercio anterior; como queda `resolved: true`, el hook salta la re-hidratación
// y muestra la variante equivocada al nuevo comercio (multisucursal cambia de sesión SIN reload).
//
// Notifica a los suscriptores montados antes de vaciar para que re-lean el snapshot: el próximo
// getSlot reinicializa SÍNCRONO desde el cache local del comercio activo (keyed por commerce_id),
// y `resolved` arranca según ese cache → re-hidrata si hace falta.
export function resetFeatureVersionStore(): void {
  const listeners: Listener[] = [];
  slots.forEach((slot) => slot.listeners.forEach((l) => listeners.push(l)));
  slots.clear();
  listeners.forEach((l) => l());
}

// Solo para tests: limpia el singleton entre casos.
export function __resetStoreForTests(): void {
  slots.clear();
}
